import {
  dateFormat,
  capitalize,
} from "@sky-it-livedata-libraries/livedata-commons";

import {
  ITeamCalendarResults,
  ITeamCompetition,
  IAdditiveFilterModel,
  IAdditiveFilterSecondaryDropdown,
} from "@sky-it-livedata/livedata-sports-design-system";
import {
  CreateTeamCalendarAndResultsInputSchema,
  CalendarAndResultsMatchDayList,
} from "appsync";
import { TeamCalendarAndResultsQuery } from "../types";
import { ErrorName, WidgetError } from "../../../utils/errors";
import { intcmpMap } from "../../../utils/intcmp";
import { isValidTeamCalendarAndResults } from "./typeguards";

const defaultSecondaryDropdown: ReadonlyArray<IAdditiveFilterSecondaryDropdown> = [
  { label: "Tutti i mesi" },
];

const defaultTeamFilterModel: ReadonlyArray<IAdditiveFilterModel> = [
  {
    name: "Tutte le Competizioni",
    secondaryDropdownModel: [...defaultSecondaryDropdown],
  },
];

// Adds DATE/MONTH as a STRING for DROPDOWN FILTER
const addMonth = (
  competitions: ReadonlyArray<CalendarAndResultsMatchDayList | null | undefined> | null | undefined,
): Array<ITeamCompetition> => {
  if (competitions?.length)
    return competitions
      .filter(
        (competition): competition is CalendarAndResultsMatchDayList =>
          competition != null,
      )
      .map((competition) => {
        const { matchList } = competition;
        return {
          ...(competition as ITeamCompetition),
          matchList: (matchList ?? []).map((match) => ({
            ...match,
            liveOnSky: match?.liveOnSky ?? undefined,
          })) as ITeamCompetition["matchList"],
          date: competition?.date ?? "",
          customCompetitionName: capitalize(
            competition?.customCompetitionName || "",
          ), // From "serie a" to "Serie A"
          month: capitalize(
            dateFormat({ date: competition?.date ?? "", format: "MMMM" }),
          ), // ITALIAN LOCALE does NOT capitalize FIRST letter of MONTHS!
        };
      });
  return [];
};

// Remove DUPLICATES from ARRAY of OBJECTS using a single KEY/VALUE
const uniqueFilter = (data: Array<any>, key: string): Array<any> => [
  ...new Map(data.map((item: any) => [item[key], item])).values(),
];

// Get all MONTH NAMES  of the passed COMPETITION and returns an ARRAY of LABELS for SECONDARY DROPDOWN
const getCompetitionMonths = (
  competitions: Array<ITeamCompetition>,
  customCompetitionName: string,
): Array<IAdditiveFilterSecondaryDropdown> => {
  const filteredCompetitions: Array<ITeamCompetition> = competitions.filter(
    (competition) =>
      competition.customCompetitionName === customCompetitionName,
  );
  const months: Array<IAdditiveFilterSecondaryDropdown> = uniqueFilter(
    filteredCompetitions,
    "month",
  ).map((competition) => ({
    label: competition.month,
  }));

  return [...defaultSecondaryDropdown, ...months];
};

// Generates DATA for both COMPETITION/MONTH dropdowns
const modelDropdown = (
  competitions: Array<ITeamCompetition>,
  uniqueCompetitions: Array<ITeamCompetition>,
  key: string,
): Array<IAdditiveFilterModel> =>
  uniqueCompetitions.reduce(
    (acc: any, competition: any) =>
      competition && competition[key]
        ? [
            ...acc,
            {
              name: competition[key],
              // UNIQUE MONTHS for each COMPETITION
              secondaryDropdownModel: [
                ...getCompetitionMonths(
                  competitions,
                  competition?.customCompetitionName ?? "",
                ),
              ],
            },
          ]
        : acc,
    [...defaultTeamFilterModel],
  );

const transformer = (
  obj: TeamCalendarAndResultsQuery,
): ITeamCalendarResults => {
  if (isValidTeamCalendarAndResults(obj)) {
    CreateTeamCalendarAndResultsInputSchema().parse(
      obj.data?.teamCalendarAndResults,
      { reportInput: true },
    );

    const { teamId = undefined, matchDayList = [] } =
      obj.data?.teamCalendarAndResults ?? {};

    // Add MONTH NAME to EACH COMPETITION
    const competitionList: Array<ITeamCompetition> = addMonth(matchDayList);

    // UNIQUE COMPETITIONS for 1st DROPDOWN
    const uniqueCompetitions: Array<ITeamCompetition> = uniqueFilter(
      competitionList,
      "customCompetitionName",
    );

    // Compute EACH UNIQUE COMPETITION month dropdown
    const competitionFilterModel: Array<IAdditiveFilterModel> =
      modelDropdown(
        competitionList,
        uniqueCompetitions,
        "customCompetitionName",
      );

    // MERGE ALL MONTHS for ALL COMPETITIONS
    const _allMonths: Array<IAdditiveFilterSecondaryDropdown> = competitionList.map(
      (competition: ITeamCompetition) => ({
        label: competition.month,
      }),
    );
    const allMonths: Array<IAdditiveFilterSecondaryDropdown> = uniqueFilter(
      [...defaultSecondaryDropdown, ..._allMonths],
      "label",
    );

    return {
      teamId,
      competitionList,
      competitionFilterModel: [
        {
          ...defaultTeamFilterModel[0],
          secondaryDropdownModel: [...uniqueFilter(allMonths, "label")], // Assigns FILTERED and UNIQUE MONTHS to DEFAULT secondary dropdown
        },
        ...competitionFilterModel.slice(1), // Removes 1st ITEM (being rewritten with uniqueFilter())
      ],
      trackingData: intcmpMap.teamCalendarAndResults,
    };
  } else {
    throw new WidgetError<ErrorName>({
      name: "NO_VALID_DATA",
      message:
        "Invalid transformer input, Error in TeamCalendarResultsQuery object",
      data: obj,
    });
  }
};

export default transformer;
